import { forwardRef, useEffect, useState } from "react";

import styled from "styled-components/macro";

import { useCardProvider } from "Payments/globalPaymentsStateHooks";
import { motion } from "framer-motion";

import useComponentIsMounted from "Utilities/Hooks/useComponentIsMounted";

import StaxCardCVVInput from "./StaxCardCVVInput";
import useCreditCardEntryState from "./state";
import {
  InputOutline,
  SmallNumberInput,
  iPhone,
  iPhoneMini,
  useErrorShakeAnimation,
} from "./styles";

const Cvc = forwardRef(
  ({ expirationInputRef, nameInputRef, zipInputRef, submitting }, ref) => {
    const { providerIsStax } = useCardProvider();
    const [isHovering, setIsHovering] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    const isMounted = useComponentIsMounted();

    const [{ cvc, cardType, canBeEdited, isEditing, name, zip }, dispatch] =
      useCreditCardEntryState();

    const cvcLength = cardType?.cvcLength;
    const cardTypeShortname = cardType?.shortname || "";

    const shouldHideCvc = canBeEdited && !isEditing;

    const setCvc = (cvc) => dispatch({ type: "Set CVC", cvc });

    const maxLength = cvcLength || 3;

    useEffect(() => {
      setCvc(cvc.substr(0, maxLength));
    }, [maxLength, cvc]);

    const { shake: errorShake, animationProps: errorAnimationProps } =
      useErrorShakeAnimation();

    const updateCvc = (e) => {
      dispatch({
        type: "Remove Multiple Error Messages",
        messages: [
          "empty_cvc",
          "empty_cvc_amex",
          "invalid_cvc",
          "invalid_cvc_amex",
        ],
      });
      const cvc = e.target.value.replace(/\D/g, "");

      if (cvc.length > maxLength) errorShake();

      setCvc(cvc.substr(0, maxLength));

      if (cvc.length === maxLength) {
        if (!name) nameInputRef.current.focus();
        else if (!zip) zipInputRef.current.focus();
      }
    };

    const onBlur = (e) => {
      setIsFocused(false);
    };

    const keyDown = (e) => {
      if (e.code === "Backspace" && cvc.length === 0) {
        expirationInputRef.current.deleteFromCVC();
      }
    };

    if (shouldHideCvc) return null;

    return (
      <Wrapper
        {...errorAnimationProps}
        onHoverStart={() => setIsHovering(true)}
        onHoverEnd={() => setIsHovering(false)}
        initial={{ opacity: isMounted ? 0 : 1 }}
        animate={{ opacity: 1 }}
        data-cy="fund-it-card-cvc-code-input"
      >
        <InputOutline
          isFocused={!submitting && isFocused}
          isHovering={!submitting && isHovering}
          placeholder="CVC"
          placeholderAlign="right"
        />
        {providerIsStax ? (
          <StaxCardCVVInput />
        ) : (
          <CvcInput
            id="cvc-input"
            value={cvc}
            ref={ref}
            type="text"
            inputMode="numeric"
            name="cvc"
            autoComplete="cc-csc"
            tabIndex={4}
            onChange={updateCvc}
            onFocus={() => setIsFocused(true)}
            onBlur={onBlur}
            onKeyDown={keyDown}
            placeholder={maxLength === 4 ? "1234" : "123"}
            useDarkPlaceholder={cardTypeShortname === "amex"}
            disabled={submitting}
          />
        )}
      </Wrapper>
    );
  }
);

Cvc.displayName = "Cvc";
export default Cvc;

const Wrapper = styled(motion.div)`
  position: relative;
  max-width: fit-content;
  flex-grow: 0;
  padding: 10px 12px 7px 12px;
  @media screen and (max-width: ${iPhone.cssWidth}) {
    padding: ${iPhone.inputPadding};
  }
  @media screen and (max-width: ${iPhoneMini.cssWidth}) {
    padding: ${iPhoneMini.inputPadding};
  }
`;

const CvcInput = styled(SmallNumberInput)`
  max-width: 50px;
  text-align: right;
`;
